'use client';

import { Box, HStack, VStack, css } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  BodyStrong,
  MetaDefault,
  MonoSmall,
} from '../../../components/typography';
import type { SlashCommandItem } from './slash-commands-registry';

const menuContainerStyles = css({
  position: 'absolute',
  bottom: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  zIndex: 50,
  backgroundColor: 'bg.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'l2',
  boxShadow: 'lg',
  maxHeight: '240px',
  overflowY: 'auto',
  padding: 1,
});

const itemStyles = css({
  width: '100%',
  paddingInline: 2.5,
  paddingBlock: 1.5,
  borderRadius: 'md',
  cursor: 'pointer',
  transition: 'background-color 0.1s ease',
  '&:hover': {
    backgroundColor: 'bg.subtle',
  },
});

const selectedItemStyles = css({
  backgroundColor: 'bg.muted',
  '&:hover': {
    backgroundColor: 'bg.muted',
  },
});

export type SlashCommandMenuProps = {
  commands: readonly SlashCommandItem[];
  selectedIndex: number;
  onSelect: (command: SlashCommandItem) => void;
  onHoverIndex?: (index: number) => void;
  emptyLabel?: string;
};

export function SlashCommandMenu({
  commands,
  selectedIndex,
  onSelect,
  onHoverIndex,
  emptyLabel,
}: SlashCommandMenuProps) {
  const t = useTranslations('designSystem.chat.slashCommands');
  const resolvedEmptyLabel = emptyLabel ?? t('noMatchingCommands');

  return (
    <Box
      role="listbox"
      aria-label={t('menuAriaLabel')}
      data-testid="slash-command-menu"
      className={menuContainerStyles}
    >
      {commands.length === 0 ? (
        <Box px={2.5} py={2}>
          <MetaDefault color="fg.muted">{resolvedEmptyLabel}</MetaDefault>
        </Box>
      ) : (
        <VStack gap={0.5} alignItems="stretch">
          {commands.map((command, index) => {
            const isSelected = index === selectedIndex;

            return (
              <Box
                key={command.id}
                role="option"
                aria-selected={isSelected}
                data-testid={`slash-command-item-${command.name}`}
                className={`${itemStyles} ${isSelected ? selectedItemStyles : ''}`}
                onMouseEnter={() => onHoverIndex?.(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  onSelect(command);
                }}
              >
                <HStack
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                  w="full"
                >
                  <HStack gap={1.5} alignItems="baseline" minW={0}>
                    <MonoSmall color="fg.default">/{command.name}</MonoSmall>
                    {command.argumentHint ? (
                      <MonoSmall color="fg.subtle">
                        {command.argumentHint}
                      </MonoSmall>
                    ) : null}
                    <BodyStrong color="fg.muted">{command.label}</BodyStrong>
                  </HStack>
                  <MetaDefault
                    color="fg.muted"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {command.description}
                  </MetaDefault>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}
